import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CategoryItem = ({ 
  category, 
  selectedCategory, 
  onCategoryChange, 
  isOpen: parentIsOpen,
  onToggle: onToggleParent,
  level = 0,
  isCollapsed = false
}) => {
  const [openChildId, setOpenChildId] = useState(null);
  const hasChildren = category.children && category.children.length > 0;
  const isExactlySelected = selectedCategory === category.id;
  
  // Recursively check if this category or any of its children is selected
  const isSelected = React.useMemo(() => {
    if (isExactlySelected) return true;
    if (!category.children) return false;
    
    const checkSelected = (nodes) => {
      return nodes.some(node => 
        node.id === selectedCategory || 
        (node.children && checkSelected(node.children))
      );
    };
    return checkSelected(category.children);
  }, [category.children, selectedCategory, isExactlySelected]);
  
  // Reset children if this item closes
  React.useEffect(() => {
    if (!parentIsOpen) {
      setOpenChildId(null);
    }
  }, [parentIsOpen]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (onToggleParent) {
      onToggleParent();
    }
  };

  const handleClick = () => {
    onCategoryChange(category.id);
    if (onToggleParent) {
      onToggleParent();
    }
  };

  return (
    <div className={`category-item-container transition-all duration-300 ${level > 0 && !isCollapsed ? 'ml-4 mt-1 border-l border-border/50 pl-2' : 'mt-2'}`}>
      <motion.div
        whileHover={{ x: isCollapsed ? 0 : 2 }}
        whileTap={{ scale: 0.99 }}
        className={`group flex items-center ${isCollapsed ? 'justify-center p-2 mt-3' : 'justify-between p-3'} rounded-xl cursor-pointer transition-all duration-300 ${
          isSelected 
            ? 'bg-primary/10 border border-primary/20 text-primary shadow-sm' 
            : 'hover:bg-primary/[0.03] border border-border/40 hover:border-primary/20'
        } ${isExactlySelected ? 'ring-1 ring-primary/30' : ''}`}
        onClick={handleClick}
        title={isCollapsed ? category.name : ''}
      >
        <div className={`flex items-center ${isCollapsed ? 'space-x-0' : 'space-x-4'} overflow-hidden`}>
          {hasChildren ? (
            <div className={`flex-shrink-0 ${isCollapsed ? 'w-12 h-12' : 'w-10 h-10'} rounded-xl flex items-center justify-center transition-all duration-300 ${
              isSelected ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20' : 'bg-muted text-text-secondary group-hover:text-primary transition-colors'
            }`}>
              <Icon name={category.icon || 'Package'} size={isCollapsed ? 22 : 18} />
            </div>
          ) : (
            isCollapsed ? (
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-muted/30 flex items-center justify-center text-text-secondary group-hover:text-primary transition-colors">
                <Icon name={category.icon || 'Circle'} size={18} />
              </div>
            ) : <div className="flex-shrink-0 w-8" />
          )}
          
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <h4 className={`truncate tracking-tight transition-all ${
                level === 0 ? 'text-base font-bold' : 
                level === 1 ? 'text-sm font-semibold' : 'text-[13px] font-medium'
              } ${isSelected ? 'text-primary' : 'text-text-secondary'}`}>
                {category.name}
              </h4>
              {level === 0 && category.description && (
                <p className="text-[11px] font-medium text-text-secondary truncate group-hover:text-text-primary/70">
                  {category.description}
                </p>
              )}
            </div>
          )}
        </div>

        {!isCollapsed && (
          <div className="flex items-center space-x-2.5">
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-lg ${
              isSelected ? 'bg-primary/20 text-primary' : 'bg-muted text-text-secondary border border-border/50'
            }`}>
              {category.count || 0}
            </span>
            {hasChildren && (
              <button
                onClick={handleToggle}
                className={`p-1.5 rounded-lg hover:bg-muted transition-transform duration-300 ${parentIsOpen ? 'rotate-180 scale-110' : ''}`}
              >
                <Icon name="ChevronDown" size={16} className="text-text-secondary" />
              </button>
            )}
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {hasChildren && parentIsOpen && !isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {category.children.map((child) => (
              <CategoryItem
                key={child.id}
                category={child}
                selectedCategory={selectedCategory}
                onCategoryChange={onCategoryChange}
                isOpen={openChildId === child.id}
                onToggle={() => setOpenChildId(child.id === openChildId ? null : child.id)}
                level={level + 1}
                isCollapsed={isCollapsed}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CategoryFilter = ({
  selectedCategory,
  onCategoryChange,
  categories,
  totalProducts = 0,
  categoryCount = 0,
  className = '',
  isCollapsed = false,
  onToggleCollapse = null
}) => {
  const effectiveCategories = Array.isArray(categories) ? categories : [];
  const [openCategoryId, setOpenCategoryId] = useState(null);

  // Initialize openCategoryId with null by default, letting the user open sections manually.
  // Previously this was auto-expanding the active category, but we've hidden it for a cleaner start.
  React.useEffect(() => {
    // Keep sidebar categories collapsed on first load
    if (openCategoryId === null && effectiveCategories.length > 0) {
      // Do nothing, keep them closed
    }
  }, [effectiveCategories, selectedCategory]);

  return (
    <div className={`space-y-4 ${className} ${isCollapsed ? 'px-1' : ''}`}>
      <div className={`flex items-center ${isCollapsed ? 'justify-center border-b border-border/50 pb-4' : 'justify-between'} mb-2`}>
        {!isCollapsed && (
          <h3 className="text-xl font-bold text-text-primary tracking-tight">
            Product Categories
          </h3>
        )}
        {onToggleCollapse && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onToggleCollapse}
            className={`p-2 rounded-xl transition-all duration-300 ${
              isCollapsed 
                ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' 
                : 'hover:bg-muted text-text-secondary'
            }`}
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            <Icon name={isCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={isCollapsed ? 20 : 18} />
          </motion.button>
        )}
      </div>

      {effectiveCategories.length === 0 ? (
        <div className="p-8 text-center bg-muted/30 rounded-xl border border-dashed border-border">
          <Icon name="Inbox" size={24} className="mx-auto text-text-secondary/30 mb-2" />
          <p className="text-xs text-text-secondary">No categories found</p>
        </div>
      ) : (
        <div className="space-y-1" role="list">
          {effectiveCategories.map((category) => (
            <CategoryItem
              key={category.id}
              category={category}
              selectedCategory={selectedCategory}
              onCategoryChange={onCategoryChange}
              isOpen={openCategoryId === category.id}
              onToggle={() => setOpenCategoryId(category.id === openCategoryId ? null : category.id)}
              level={0}
              isCollapsed={isCollapsed}
            />
          ))}
        </div>
      )}


    </div>
  );
};

export default CategoryFilter;